import { flushSync } from 'react-dom';
import { Transition } from 'react-transition-group';

type TransitionEndHandler = (event?: unknown) => void;

type TransitionInternals = {
    onTransitionEnd: (this: unknown, timeout: unknown, handler: TransitionEndHandler) => void;
    __unitySyncCompletion?: boolean;
};

/**
 * Commit the end of every MUI enter/exit animation as a synchronous update.
 *
 * React 18 lets a default-priority update (react-transition-group's end-of-animation
 * timer) wait behind an in-progress transition render (every router navigation is one).
 * If that render re-renders the Transition class with an otherwise empty update queue,
 * react-dom 18.x drops the waiting update's lane (class processUpdateQueue only keeps
 * interleaved lanes when it has base updates; hooks are unaffected), so the status never
 * reaches `exited`. A closing Dialog, Menu or Popover then stays mounted at opacity 0
 * over the whole app, hides it from assistive technology and swallows every click.
 * A sync update interrupts the transition render instead of queueing behind it.
 * Remove when the app moves to React 19.
 */
export const installTransitionCompletionGuard = (): void => {
    const prototype = Transition.prototype as unknown as TransitionInternals;
    if (prototype.__unitySyncCompletion || typeof prototype.onTransitionEnd !== 'function') {
        return;
    }

    const onTransitionEnd = prototype.onTransitionEnd;
    prototype.onTransitionEnd = function guardedTransitionEnd(timeout, handler) {
        return onTransitionEnd.call(this, timeout, (event?: unknown) => flushSync(() => handler(event)));
    };
    prototype.__unitySyncCompletion = true;
};
